import React, { createContext, useContext, useEffect, useState } from 'react';

// Invoice-based ordering cart. Items are { slug, configurationId, qty } —
// no prices exist in the product data, so the cart produces an order
// request which Hestia confirms and invoices. Persisted to localStorage.
const CartContext = createContext(null);
const STORAGE_KEY = 'hestia_cart';

const loadCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items.filter((i) => i && (i.slug || i.cid)) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // private mode / storage full — cart still works in memory
    }
  }, [items]);

  const key = (slug, configurationId) => `${slug}::${configurationId || 'single'}`;

  const addItem = (slug, configurationId = 'single', qty = 1) => {
    setItems((prev) => {
      const k = key(slug, configurationId);
      const existing = prev.find((i) => key(i.slug, i.configurationId) === k);
      if (existing) {
        return prev.map((i) =>
          key(i.slug, i.configurationId) === k ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { slug, configurationId: configurationId || 'single', qty }];
    });
  };

  const updateQty = (slug, configurationId, qty) => {
    const k = key(slug, configurationId);
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => key(i.slug, i.configurationId) !== k));
    } else {
      setItems((prev) =>
        prev.map((i) => (key(i.slug, i.configurationId) === k ? { ...i, qty } : i))
      );
    }
  };

  const removeItem = (slug, configurationId) => {
    const k = key(slug, configurationId);
    setItems((prev) => prev.filter((i) => key(i.slug, i.configurationId) !== k));
  };

  // Configured door from /configurator — carries the full spec + calculated
  // retail price, confidence and requiresReview flag. Keyed by its config so
  // two different configurations never merge.
  const addConfiguredItem = (config, qty = 1) => {
    const cid = JSON.stringify(config);
    setItems((prev) => {
      const existing = prev.find((i) => i.configured && i.cid === cid);
      if (existing) {
        return prev.map((i) => (i.configured && i.cid === cid ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, { configured: true, cid, config, qty }];
    });
  };

  const updateConfiguredQty = (cid, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => !(i.configured && i.cid === cid)));
    } else {
      setItems((prev) => prev.map((i) => (i.configured && i.cid === cid ? { ...i, qty } : i)));
    }
  };

  const removeConfiguredItem = (cid) => {
    setItems((prev) => prev.filter((i) => !(i.configured && i.cid === cid)));
  };

  const clearCart = () => setItems([]);

  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, addConfiguredItem, updateConfiguredQty, removeConfiguredItem, clearCart, totalItems }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
